import { ApolloState } from 'apollo/types';
import { AppConfigState } from 'config/types';
import {
  ChangeType,
  DocumentKey,
  File as GqlFile,
  WorkerErrorInfo,
} from 'apollo/generated/client-operations';
import { SelectOptionElement } from '@odin-labs/components';

export type Maybe<T> = T | null;

export interface ClientAppState {
  apolloState: ApolloState;
  appConfigState?: AppConfigState;
}

export interface CustomWindow extends Window {
  __CLIENT_APP_STATE__?: ClientAppState;
}

/**
 * Returns all fields for a specified type <T>.
 */
export const getFields = <T>(): { [P in keyof T]: P } => {
  return new Proxy(
    {},
    {
      get: (_target, prop): string | number | symbol => prop,
    },
  ) as {
    [P in keyof T]: P;
  };
};

export type Entries<T> = {
  [K in keyof T]: [K, T[K]];
}[keyof T][];

export { ChangeType };

export type Editable<T> = T & {
  changeType?: ChangeType;
};

export type EditType = 'create' | 'update';
export type ChangeActionType = EditType | 'remove';
export type Change<T> = { type: ChangeActionType; item?: T };

export type ServerFile = Omit<GqlFile, 'uploadedAt'>;
export type TransformableFile = File & { isTransformationInProgress?: boolean };
export type InputFile = TransformableFile | ServerFile;

export type AcknowledgmentProgress = {
  fraction: number;
  value?: number;
};

export type StringEnum = { [s: string]: string };

// :::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::
// :::                                                                                       :::
// ::: Union Type Merging in Typescript                                                      :::
// ::: https://dev.to/lucianbc/union-type-merging-in-typescript-9al                          :::
type AllKeys<T> = T extends any ? keyof T : never;
type PickType<T, K extends AllKeys<T>> = T extends { [k in K]?: any }
  ? T[K]
  : undefined;
/** Merge a type union (of objects) into a single object type. */
export type MergeUnionType<T extends object> = {
  [k in AllKeys<T>]: PickType<T, k>;
};
// :::                                                                                       :::
// :::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::

export type EmailAlreadyExistsErrorExtensions = { workerId?: string };
export type PhoneAlreadyExistsErrorExtensions = { workerId?: string };

export type WorkerCardErrorInfo = { workerCardId: string } & WorkerErrorInfo;

export type WorkerCardAlreadyExistsErrorExtensions = {
  workerCardInfo?: WorkerCardErrorInfo[];
  canBeReactivated?: boolean;
  scope: 'onSameWorker' | 'onDifferentWorker' | 'onAnyWorker';
};

export type WorkerAlreadyExistsErrorExtensions = {
  workerInfo?: WorkerErrorInfo[];
};

export type ReplaceSelectOptionElement<T> = {
  [K in keyof T]: T[K] extends SelectOptionElement<infer U>
    ? U
    : T[K] extends SelectOptionElement<infer U>[]
    ? U[]
    : T[K];
};

// Utility type to extract all field types
export type FieldTypes<T> = T[keyof T];

/**
 * Worker document type which can have multiple instances on a jobsite.
 *
 * A worker document type should be added to this union if:
 * - The business logic allows or expects a jobsite to have more than one instance of this document type.
 * - For example, a jobsite may have multiple safety videos which need to be acknowledged by different workers.
 * - Single-instance document types (where a jobsite can only have one) should NOT be added here.
 *
 * When adding a new worker document type:
 * - Ensure that the rest of the codebase supports multiple instances for this type.
 * - Update any related logic that depends on this union.
 */
export type MultiInstanceDocTypeKey = DocumentKey.JobsiteSafetyVideo;
export type SingleInstanceDocTypeKey = Exclude<
  DocumentKey,
  MultiInstanceDocTypeKey
>;

/**
 * Composite key pattern for document types:
 * - SingleInstanceDocTypeKey: a key for documents that have only one instance per jobsite.
 * - `${MultiInstanceDocTypeKey}-${string}`: a key for multi-instance documents.
 *   - MultiInstanceDocTypeKey: the base document type (e.g., jobsite-safety-video).
 *   - The string segment: typically represents the date-time when the document instance was created.
 * Example: `jobsite-safety-video-20251105-014210-157`.
 */
export type DocTypeKey =
  | SingleInstanceDocTypeKey
  | `${MultiInstanceDocTypeKey}-${string}`;

/**
 *
 * Create a union type by combining string literal types with `string` without sacrificing auto-completion in IDEs for the literal type part of the union.
 * Currently, when a union type of `string` is combined with literal types, TypeScript loses all information about the combined literals.
 * Thus, when such type is used in an IDE with autocompletion, no suggestions are made for the declared literals.
 */
export type LooseAutocomplete<T> = T | (string & Record<never, never>);

// Use the below helper type when you want to use a type other than string
/**
 *
 * Create a union type by combining primitive types and literal types without sacrificing auto-completion in IDEs for the literal type part of the union.
 * Currently, when a union type of a primitive type is combined with literal types, TypeScript loses all information about the combined literals.
 * Thus, when such type is used in an IDE with autocompletion, no suggestions are made for the declared literals.
 * This type is a workaround for [Microsoft/TypeScript#29729](https://github.com/Microsoft/TypeScript/issues/29729). It will be removed as soon as it's not needed anymore.
 * https://github.com/sindresorhus/type-fest/blob/main/source/literal-union.d.ts
 */
export type LiteralUnion<LiteralType, BaseType = string> =
  | LiteralType
  | (BaseType & Record<never, never>);
